import { Settings } from "../../Settings";
import BigNum from "../math/BigNum";
import PickUpgrade from "../upgrade/ore/PickUpgrade";
import PickUpgradeCollection from "../upgrade/ore/PickUpgradeCollection";
import Values from "../value/Values";
import BaseAutomation from "./BaseAutomation";

export default class PickUpgradeAutomation extends BaseAutomation {
    /**
     * @param {GameTime} gameTime
     * @param {PickUpgradeCollection} pickUpgradeCollection
     * @param {Values} values
     */
    constructor(gameTime, pickUpgradeCollection, values) {
        super(gameTime, values.pickAutomationUnlocked, 
            values.pickAutomationRateUpgrades, 
            Settings.automation.baseUpgradeDelayTurns, 
            Settings.automation.turnsPerRateUpgrade);

        /**
         * @constant
         * @type {PickUpgradeCollection}
         */
        this.pickUpgradeCollection = pickUpgradeCollection;

        /**
         * @private
         * @constant
         * @type {PickUpgrade[]}
         */
        this.validUpgrades = [];


        /**
         * @constant
         * @type {BigNum}
         */
        this.workingScore = new BigNum(0);
        /**
         * @constant
         * @type {BigNum}
         */
        this.bestScore = new BigNum(0);

        /**
         * @type {boolean}
         */
        this.additive = true;
    }

    /**
     * @override
     */
    purchaseUpgrade() {
        const upgrades = this.pickUpgradeCollection.upgrades;
        if (upgrades.length === 0) {
            return;
        }

        // Toggle
        this.additive = !this.additive;

        this.findValidUpgrades();
        if (this.validUpgrades.length === 0) {
            return;
        }

        const upgrade = this.selectBestUpgrade(this.additive);
        if (!upgrade) {
            return;
        }

        // Unlike mined upgrades, pick upgrades aren't removed when purchased.
        upgrade.purchase();
    }

    /**
     * @private
     */
    findValidUpgrades() {
        this.validUpgrades.length = 0;

        const upgrades = this.pickUpgradeCollection.upgrades;
        for (let i = 0; i < upgrades.length; i++) {
            const upgrade = upgrades[i];
            if (this.isValidUpgrade(upgrade)) {
                this.validUpgrades.push(upgrade);
            }
        }
    }

    /**
     * @private
     * @param {boolean} additive
     * @returns {PickUpgrade}
     */
    selectBestUpgrade(additive) {

        // Don't compare additive scores against multiplicative scores. That wouldn't make sense.

        const bestAdditiveUpgrade = this.selectBestUpgradeByType(true);
        const bestMultiplicativeUpgrade = this.selectBestUpgradeByType(false);

        if (!bestAdditiveUpgrade && !bestMultiplicativeUpgrade) {
            return null;
        }
        else if (!bestAdditiveUpgrade) {
            return bestMultiplicativeUpgrade;
        }
        else if (!bestMultiplicativeUpgrade) {
            return bestAdditiveUpgrade;
        }
        else if (additive) {
            return bestAdditiveUpgrade;
        }
        else {
            return bestMultiplicativeUpgrade;
        }
    }
    

    /**
     * @private
     * @param {boolean} additive True for additive upgrades, false for multplicative
     * @returns {PickUpgrade}
     */
    selectBestUpgradeByType(additive) {
        this.bestScore.setZero();

        let bestUpgrade = null;

        for (let i = 0; i < this.validUpgrades.length; i++) {
            const upgrade = this.validUpgrades[i];
            if (upgrade.additiveUpgrade !== additive) {
                continue;
            }

            const score = this.getUpgradeScore(upgrade);

            if (!bestUpgrade || score.greaterThan(this.bestScore)) {
                bestUpgrade = upgrade;
                this.bestScore.copy(score);
            }
        }

        return bestUpgrade;
    }


    /**
     * @private
     * @param {PickUpgrade} pickUpgrade 
     * @returns {BigNum}
     */
    getUpgradeScore(pickUpgrade) {
        // score:  value gained / cost

        //pickUpgrade.nextActualPurchaseCount;
        //pickUpgrade.valuePerUpgrade

        this.workingScore.copy(pickUpgrade.valuePerUpgrade);
        this.workingScore.mulNumber(pickUpgrade.nextActualPurchaseCount);
        this.workingScore.div(pickUpgrade.getCost());

        return this.workingScore;
    }

    /**
     * @private
     * @param {PickUpgrade} upgrade 
     * @returns {boolean}
     */
    isValidUpgrade(upgrade) {
        if (!upgrade) {
            return false;
        }

        // isVisible() must be true
        // isPurchasable() must be true

        // NOTE: This is using the USER's purchase amount setting.

        return upgrade.isVisible() && upgrade.isPurchasable();
    }
}